import React, {useEffect, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    Heading,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    useBreakpointValue,
    IconButton,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import Link from '@salesforce/retail-react-app/app/components/link'
import RecommendedProducts from '../../components/recommended-products'

//import {LockIcon} from '@salesforce/retail-react-app/app/components/icons'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    getPriceData,
    getDisplayVariationValues
} from '@salesforce/retail-react-app/app/utils/product-utils'
// import DisplayPrice from '@salesforce/retail-react-app/app/components/display-price'
import DisplayPrice from '../../components/display-price'

import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {
    bodyStyle,
    buttonsSection,
    carSubTotal,
    carSubTotalValue,
    checkoutButton,
    closeButton,
    contentStyle,
    detailsWrapper,
    footerButtonContainer,
    footerSection,
    footerSubCartText,
    headerStyle,
    headerTextStyle,
    imageWrapper,
    productDetailsInnerSection,
    productDetailsSection,
    productTitle,
    recommendWrapper,
    variationBox,
    variationItem,
    viewCartButton
} from './index.style'
import {useLocation} from 'react-router-dom'
import useSitePreference from '../../hooks/use-site-preference'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useSizeMappingData from '../../utills/getProductSizeMapping'
import {PDP_RECOMMENDATION} from '../../constants'

export const AddToCartModal = () => {
    const {isOpen, onClose, data} = useAddToCartModalContext()
    const {product, itemsAdded = [], selectedQuantity} = data || {}
    const isProductABundle = product?.type?.bundle
    const {data: customer} = useCurrentCustomer()
    const sizeMappingData = useSizeMappingData()
    const {pathname} = useLocation()
    const navigate = useNavigation()

    const {data: sitePrefData} = useSitePreference('pdpVariationCategories,suggestedForYou')
    const pdpVariationCategory = sitePrefData?.pdpVariationCategories
    const suggestedForYou = sitePrefData?.suggestedForYou

    const intl = useIntl()
    const {
        data: basket = {},
        derivedData: {totalItems}
    } = useCurrentBasket()
    const size = useBreakpointValue({base: 'full', md: '4xl', lg: '4xl', xl: '5xl'})
    const {currency, productSubTotal} = basket

    const productItemsQuantity =
        basket?.productItems?.reduce((sum, item) => sum + item.quantity, 0) || 0
    const giftCertificateItemsCount = basket?.giftCertificateItems?.length || 0
    const totalItemsWithGiftCards = productItemsQuantity + giftCertificateItemsCount

    const numberOfItemsAdded = isProductABundle
        ? selectedQuantity
        : itemsAdded.reduce((acc, {quantity}) => acc + quantity, 0)

    // Refs for accessibility
    const modalRef = useRef(null)
    const firstFocusableElementRef = useRef(null)
    const liveRegionRef = useRef(null)

    // Focus management: Move focus into modal when it opens
    useEffect(() => {
        if (isOpen && modalRef.current) {
            // Find the first focusable element in the modal
            const focusableElements = modalRef.current.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            if (focusableElements.length > 0) {
                firstFocusableElementRef.current = focusableElements[0]
                // Focus the close button or first focusable element
                setTimeout(() => {
                    firstFocusableElementRef.current?.focus()
                }, 100)
            }
        }
    }, [isOpen])

    // Announce cart update to screen readers
    useEffect(() => {
        if (isOpen && liveRegionRef.current) {
            const announcement = intl.formatMessage(
                {
                    defaultMessage:
                        'Item added to cart. {totalItems} {totalItems, plural, one {item} other {items}} in cart. Total {total}',
                    id: 'add_to_cart_modal.aria.cart_updated'
                },
                {
                    totalItems,
                    total: productSubTotal
                        ? intl.formatNumber(productSubTotal, {
                              style: 'currency',
                              currency: currency
                          })
                        : ''
                }
            )
            liveRegionRef.current.textContent = announcement
        }
    }, [isOpen, totalItems, productSubTotal, currency, intl])

    // Focus trap: Keep focus within modal
    const handleKeyDown = useCallback(
        (event) => {
            if (!isOpen || !modalRef.current) return

            // Handle ESC key
            if (event.key === 'Escape') {
                onClose()
                return
            }

            // Handle Tab key for focus trap
            if (event.key === 'Tab') {
                const focusableElements = modalRef.current.querySelectorAll(
                    'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
                )
                const firstElement = focusableElements[0]
                const lastElement = focusableElements[focusableElements.length - 1]

                if (event.shiftKey) {
                    // Shift + Tab
                    if (document.activeElement === firstElement) {
                        event.preventDefault()
                        lastElement?.focus()
                    }
                } else {
                    // Tab
                    if (document.activeElement === lastElement) {
                        event.preventDefault()
                        firstElement?.focus()
                    }
                }
            }
        },
        [isOpen, onClose]
    )

    // Add keydown event listener for focus trap
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            return () => {
                document.removeEventListener('keydown', handleKeyDown)
            }
        }
    }, [isOpen, handleKeyDown])

    if (!isOpen) {
        return null
    }

    if (pathname.startsWith('/cart')) {
        onClose()
        navigate('/cart')
        window.scrollTo(0, 0)
        return null
    }

    const bundleImage = findImageGroupBy(product.imageGroups, {
        viewType: 'small'
    })?.images?.[0]

    return (
        <>
            {/* ARIA live region for screen reader announcements */}
            <VisuallyHidden>
                <div ref={liveRegionRef} role="status" aria-live="polite" aria-atomic="true" />
            </VisuallyHidden>

            <Modal
                size={size}
                isOpen={isOpen}
                onClose={onClose}
                scrollBehavior="inside"
                isCentered
                closeOnOverlayClick={true}
                closeOnEsc={true}
            >
                <ModalOverlay />
                <ModalContent
                    ref={modalRef}
                    margin="0"
                    borderRadius={{base: 'none', md: 'base'}}
                    bgColor="gray.50"
                    containerProps={{
                        'data-testid': 'add-to-cart-modal',
                        role: 'dialog',
                        'aria-modal': 'true',
                        'aria-labelledby': 'modal-title',
                        'aria-describedby': 'modal-description'
                    }}
                    sx={contentStyle}
                >
                    <ModalHeader sx={headerStyle} paddingY="8" bgColor="white">
                        <Heading id="modal-title" sx={headerTextStyle} as="h1" fontSize="2xl">
                            {intl.formatMessage(
                                {
                                    defaultMessage:
                                        '{quantity} {quantity, plural, one {item} other {items}} added to cart',
                                    id: 'add_to_cart_modal.info.added_to_cart'
                                },
                                {quantity: numberOfItemsAdded}
                            )}
                        </Heading>
                    </ModalHeader>
                    <IconButton
                        icon={
                            <img
                                src={getAssetUrl('static/img/pdp/close.svg')}
                                height="15"
                                width="15"
                                alt=""
                                aria-hidden="true"
                            />
                        }
                        variant="unstyled"
                        onClick={onClose}
                        sx={closeButton}
                        aria-label={intl.formatMessage({
                            defaultMessage: 'Close modal',
                            id: 'add_to_cart_modal.button.close_modal'
                        })}
                    />
                    <ModalBody
                        sx={bodyStyle}
                        bgColor="white"
                        padding="0"
                        marginBottom={{base: 40, lg: 0}}
                    >
                        {/* Hidden description for screen readers */}
                        <VisuallyHidden id="modal-description">
                            {intl.formatMessage(
                                {
                                    defaultMessage:
                                        'Shopping cart modal. {totalItems} {totalItems, plural, one {item} other {items}} in cart. Cart subtotal: {total}',
                                    id: 'add_to_cart_modal.aria.modal_description'
                                },
                                {
                                    totalItems,
                                    total: productSubTotal
                                        ? intl.formatNumber(productSubTotal, {
                                              style: 'currency',
                                              currency: currency
                                          })
                                        : ''
                                }
                            )}
                        </VisuallyHidden>
                        <Flex
                            flexDirection={{base: 'column', lg: 'row'}}
                            justifyContent="space-between"
                        >
                            <Box sx={productDetailsSection}>
                                {isProductABundle && (
                                    <Flex
                                        key={product.id}
                                        justifyContent="space-between"
                                        sx={productDetailsInnerSection}
                                    >
                                        <Flex gridGap="4">
                                            <Box sx={imageWrapper} flex="none">
                                                <AspectRatio ratio="1">
                                                    <img src={bundleImage} alt={product.name} />
                                                </AspectRatio>
                                            </Box>

                                            <Box>
                                                <Text fontWeight="700">{product.name}</Text>
                                                <Box
                                                    color="gray.600"
                                                    fontSize="sm"
                                                    fontWeight="400"
                                                >
                                                    <Heading
                                                        as="h2"
                                                        fontSize="md"
                                                        fontFamily="body"
                                                        fontWeight="700"
                                                        sx={productTitle}
                                                    >
                                                        {product.name}
                                                    </Heading>
                                                </Box>
                                                <Flex
                                                    flexDirection="column"
                                                    justifyContent="space-between"
                                                    sx={variationBox}
                                                >
                                                    {itemsAdded.map(
                                                        ({product, variant, quantity}) => {
                                                            const variationAttributeValues =
                                                                getDisplayVariationValues(
                                                                    product.variationAttributes,
                                                                    variant.variationValues
                                                                )
                                                            return (
                                                                <Box key={variant.productId}>
                                                                    <Text
                                                                        color="gray.700"
                                                                        fontWeight="700"
                                                                        fontSize="sm"
                                                                        sx={variationItem}
                                                                    >
                                                                        {product.name}{' '}
                                                                        {quantity > 1
                                                                            ? `(${quantity})`
                                                                            : ''}
                                                                    </Text>
                                                                    <Box
                                                                        color="gray.600"
                                                                        fontSize="sm"
                                                                        fontWeight="500"
                                                                    >
                                                                        {Object.entries(
                                                                            variationAttributeValues
                                                                        ).map(([name, value]) => {
                                                                            return (
                                                                                <Text
                                                                                    key={value}
                                                                                    sx={
                                                                                        variationItem
                                                                                    }
                                                                                >
                                                                                    {name}: {value}
                                                                                </Text>
                                                                            )
                                                                        })}
                                                                    </Box>
                                                                </Box>
                                                            )
                                                        }
                                                    )}
                                                </Flex>
                                            </Box>
                                        </Flex>

                                        <Box flex="none" alignSelf="flex-end" fontWeight="600">
                                            <Text>
                                                {intl.formatNumber(
                                                    product.price * numberOfItemsAdded,
                                                    {
                                                        style: 'currency',
                                                        currency: currency
                                                    }
                                                )}
                                            </Text>
                                        </Box>
                                    </Flex>
                                )}
                                {!isProductABundle &&
                                    itemsAdded.map(({product, variant, quantity}, index) => {
                                        const image =
                                            product?.c_cloudinary?.cartImage?.url?.replaceAll(
                                                'dpr_auto',
                                                'dpr_2.0'
                                            )
                                        const priceData = getPriceData(product, {quantity})
                                        const variationValues = variant.variationValues

                                        const productAttributes = {}
                                        const needChange = pdpVariationCategory?.includes(
                                            product?.primaryCategoryId ||
                                                product?.c_primaryCatergoryID
                                        )

                                        if (variationValues?.color) {
                                            const colorAttr = product.variationAttributes?.find(
                                                (attr) => attr.id === 'color'
                                            )
                                            const colorValue = colorAttr?.values?.find(
                                                (val) => val.value === variationValues.color
                                            )
                                            if (colorValue?.name) {
                                                productAttributes['Color'] = colorValue.name
                                            }
                                        }

                                        if (variationValues?.waist)
                                            productAttributes['Waist'] = variationValues?.waist

                                        if (variationValues?.size) {
                                            if (needChange) {
                                                productAttributes['Waist'] = variationValues?.size
                                            } else {
                                                productAttributes['Size'] = variationValues?.size
                                            }
                                        }

                                        if (variationValues?.width) {
                                            if (needChange) {
                                                productAttributes['Length'] = variationValues?.width
                                            } else {
                                                productAttributes['Width'] = variationValues?.width
                                            }
                                        }

                                        if (variationValues?.length)
                                            productAttributes['Length'] = variationValues?.length

                                        return (
                                            <Flex
                                                key={variant.productId}
                                                justifyContent="space-between"
                                                sx={productDetailsInnerSection}
                                            >
                                                <Flex gridGap="5" sx={detailsWrapper}>
                                                    <Box sx={imageWrapper} flex="none">
                                                        <AspectRatio ratio="1">
                                                            <img src={image} alt={product.name} />
                                                        </AspectRatio>
                                                    </Box>

                                                    <Box>
                                                        <Heading
                                                            as="h2"
                                                            fontSize="md"
                                                            fontFamily="body"
                                                            fontWeight="700"
                                                            sx={productTitle}
                                                        >
                                                            {product.name}
                                                        </Heading>
                                                        <Box
                                                            color="gray.600"
                                                            fontSize="sm"
                                                            fontWeight="400"
                                                            sx={variationBox}
                                                        >
                                                            {Object.entries(productAttributes).map(
                                                                ([name, value]) => {
                                                                    return (
                                                                        <Text
                                                                            sx={variationItem}
                                                                            key={value}
                                                                        >
                                                                            {name}:{' '}
                                                                            {name == 'Color'
                                                                                ? value
                                                                                      ?.charAt(0)
                                                                                      ?.toUpperCase() +
                                                                                  value
                                                                                      ?.slice(1)
                                                                                      ?.toLowerCase()
                                                                                : sizeMappingData[
                                                                                      value
                                                                                  ] || value}
                                                                        </Text>
                                                                    )
                                                                }
                                                            )}
                                                            <Text sx={variationItem}>
                                                                {intl.formatMessage({
                                                                    defaultMessage: 'Qty',
                                                                    id: 'add_to_cart_modal.label.quantity'
                                                                })}
                                                                : {quantity}
                                                            </Text>
                                                        </Box>
                                                        <DisplayPrice
                                                            priceData={priceData}
                                                            quantity={quantity}
                                                            currency={currency}
                                                            isFromAddToCartPopup={true}
                                                        />
                                                    </Box>
                                                </Flex>
                                            </Flex>
                                        )
                                    })}
                            </Box>
                            <Box display={['none', 'none', 'none', 'block']} sx={buttonsSection}>
                                <Box justifyContent="space-between">
                                    <Text fontWeight="700" sx={carSubTotal}>
                                        {intl.formatMessage(
                                            {
                                                defaultMessage:
                                                    'Cart Subtotal ({totalItems, plural, one {# item} other {# items}}):',
                                                id: 'add_to_cart_modal.label.cart_subtotalmain'
                                            },
                                            {totalItems: totalItemsWithGiftCards}
                                        )}
                                    </Text>
                                    <Text alignSelf="flex-end" sx={carSubTotalValue}>
                                        {productSubTotal &&
                                            intl.formatNumber(productSubTotal, {
                                                style: 'currency',
                                                currency: currency
                                            })}
                                    </Text>
                                </Box>
                                <Box>
                                    <Button
                                        as={Link}
                                        to="/cart"
                                        width="100%"
                                        variant="outline"
                                        sx={viewCartButton}
                                    >
                                        {intl.formatMessage({
                                            defaultMessage: 'View Cart',
                                            id: 'add_to_cart_modal.link.view_cart'
                                        })}
                                    </Button>

                                    <Button
                                        as={Link}
                                        to={'/checkout/'}
                                        width="100%"
                                        variant="solid"
                                        sx={checkoutButton}
                                    >
                                        {intl.formatMessage({
                                            defaultMessage: 'Proceed to Checkout',
                                            id: 'add_to_cart_modal.link.checkout'
                                        })}
                                    </Button>
                                </Box>
                            </Box>
                        </Flex>
                        <Box sx={recommendWrapper}>
                            <RecommendedProducts
                                title={suggestedForYou}
                                recommender={PDP_RECOMMENDATION}
                                fetchKey={product?.id || product?.productId}
                                products={[product]}
                                isFromAddToCart={true}
                                showQuickView={false}
                            />
                        </Box>
                    </ModalBody>
                    <ModalFooter
                        position="fixed"
                        bg="white"
                        width="100%"
                        display={['block', 'block', 'block', 'none']}
                        p={[4, 4, 6]}
                        left={0}
                        bottom={0}
                        sx={footerSection}
                    >
                        <Flex justifyContent="center">
                            <Text fontWeight="700" sx={footerSubCartText}>
                                {intl.formatMessage(
                                    {
                                        defaultMessage:
                                            'Cart Subtotal ({totalItems, plural, one {# item} other {# items}}):',
                                        id: 'add_to_cart_modal.label.cart_subtotalFooter'
                                    },
                                    {totalItems: totalItemsWithGiftCards}
                                )}{' '}
                                <span style={{fontWeight: '400'}}>
                                    {productSubTotal &&
                                        intl.formatNumber(productSubTotal, {
                                            style: 'currency',
                                            currency: currency
                                        })}
                                </span>
                            </Text>
                        </Flex>
                        <Box sx={footerButtonContainer}>
                            <Button
                                as={Link}
                                to="/cart"
                                width="100%"
                                variant="outline"
                                sx={viewCartButton}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'View Cart',
                                    id: 'add_to_cart_modal.link.view_cart'
                                })}
                            </Button>

                            <Button
                                as={Link}
                                to={'/checkout/'}
                                width="100%"
                                variant="solid"
                                sx={checkoutButton}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Proceed to Checkout',
                                    id: 'add_to_cart_modal.link.checkout'
                                })}
                            </Button>
                        </Box>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    )
}

AddToCartModal.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array
    }),
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object
    }),
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any
}
